export type StudioNodeType = 'frame' | 'stack' | 'text' | 'button' | 'media' | 'badge';
export type StudioComponentCategory = 'marketing' | 'commerce' | 'content' | 'navigation' | 'utility';
export type StudioComponentStatus = 'draft' | 'ready';
export type StudioComponentSource = 'built-in' | 'custom';
export type StudioAnimationName = 'none' | 'fade-up' | 'fade-in' | 'pop-in' | 'pulse' | 'float';

export interface StudioNodeTransform {
  translateX: number;
  translateY: number;
  scaleX: number;
  scaleY: number;
  rotate: number;
  skewX: number;
  skewY: number;
}

export interface StudioNodeAnimation {
  name: StudioAnimationName;
  durationMs: number;
  delayMs: number;
  easing: string;
  iterationCount: string;
}

export interface StudioNodeStyle {
  display: string;
  flexDirection: string;
  flexWrap: string;
  justifyContent: string;
  alignItems: string;
  alignSelf: string;
  flexGrow: number;
  flexShrink: number;
  order: number;
  gap: string;
  width: string;
  height: string;
  aspectRatio: string;
  minWidth: string;
  maxWidth: string;
  minHeight: string;
  maxHeight: string;
  flexBasis: string;
  padding: string;
  margin: string;
  backgroundColor: string;
  backgroundImage: string;
  backgroundPosition: string;
  backgroundSize: string;
  backgroundRepeat: string;
  color: string;
  fontFamily: string;
  fontSize: string;
  fontWeight: string;
  fontStyle: string;
  lineHeight: string;
  letterSpacing: string;
  textAlign: string;
  textTransform: string;
  textDecoration: string;
  whiteSpace: string;
  wordBreak: string;
  borderWidth: string;
  borderStyle: string;
  borderColor: string;
  borderRadius: string;
  outlineWidth: string;
  outlineStyle: string;
  outlineColor: string;
  boxShadow: string;
  filter: string;
  backdropFilter: string;
  opacity: number;
  position: 'static' | 'relative' | 'absolute' | 'sticky' | 'fixed';
  top: string;
  right: string;
  bottom: string;
  left: string;
  zIndex: number;
  overflow: string;
  overflowX: string;
  overflowY: string;
  visibility: string;
  cursor: string;
  pointerEvents: string;
  objectFit: string;
  objectPosition: string;
  transform: StudioNodeTransform;
  animation: StudioNodeAnimation;
  customCss: string;
}

export interface StudioNodeContent {
  eyebrow?: string;
  title?: string;
  text?: string;
  secondaryText?: string;
  buttonText?: string;
  badgeText?: string;
  mediaLabel?: string;
  caption?: string;
  mediaUrl?: string;
  mediaAlt?: string;
  href?: string;
}

export interface StudioNode {
  id: string;
  type: StudioNodeType;
  label: string;
  hidden: boolean;
  locked: boolean;
  content: StudioNodeContent;
  style: StudioNodeStyle;
  children: StudioNode[];
}

export interface StudioComponentDefinition {
  id: string;
  slug: string;
  name: string;
  source: StudioComponentSource;
  category: StudioComponentCategory;
  description: string;
  tags: string[];
  version: number;
  status: StudioComponentStatus;
  usageCount: number;
  updatedAt: string;
  root: StudioNode;
}

export interface StudioInsertPreset {
  id: string;
  label: string;
  description: string;
  accent: string;
  icon: string;
}

export const STUDIO_INSERT_PRESETS: StudioInsertPreset[] = [
  {
    id: 'text-block',
    label: 'Text node block',
    description: 'Heading, body copy, and optional supporting text nodes.',
    accent: '#2563eb',
    icon: 'text-outline'
  },
  {
    id: 'button-row',
    label: 'Button group',
    description: 'Primary and secondary button nodes inside a flex row.',
    accent: '#0f766e',
    icon: 'radio-button-on-outline'
  },
  {
    id: 'media-card',
    label: 'Media container',
    description: 'Media placeholder shell with caption and surface styling.',
    accent: '#7c3aed',
    icon: 'image-outline'
  },
  {
    id: 'feature-stack',
    label: 'Stack container',
    description: 'Stack wrapper for grouped child nodes and nested layout.',
    accent: '#ea580c',
    icon: 'layers-outline'
  },
  {
    id: 'badge-cluster',
    label: 'Badge group',
    description: 'Inline badge nodes for trust, labels, or compact metadata.',
    accent: '#ca8a04',
    icon: 'pricetags-outline'
  }
];

export function createStudioId(prefix: string): string {
  return createPrefixedUuidV7(prefix);
}

export function isStudioContainerNode(node: StudioNode | null | undefined): boolean {
  return !!node && (node.type === 'frame' || node.type === 'stack');
}

export function studioDropListId(nodeId: string): string {
  return `studio-drop-${nodeId}`;
}

export function collectStudioDropListIds(root: StudioNode): string[] {
  const ids: string[] = [];

  walkStudioNodes(root, (node) => {
    if (isStudioContainerNode(node)) {
      ids.push(studioDropListId(node.id));
    }
  });

  return ids;
}

export function walkStudioNodes(node: StudioNode, visitor: (node: StudioNode) => void): void {
  visitor(node);
  node.children.forEach((child) => walkStudioNodes(child, visitor));
}

export function findStudioNode(root: StudioNode, nodeId: string): StudioNode | null {
  if (root.id === nodeId) {
    return root;
  }

  for (const child of root.children) {
    const match = findStudioNode(child, nodeId);
    if (match) {
      return match;
    }
  }

  return null;
}

export function findStudioParentNode(root: StudioNode, nodeId: string): StudioNode | null {
  for (const child of root.children) {
    if (child.id === nodeId) {
      return root;
    }

    const match = findStudioParentNode(child, nodeId);
    if (match) {
      return match;
    }
  }

  return null;
}

export function parseStudioCustomCss(cssText: string): Record<string, string> {
  return cssText
    .split(';')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .reduce<Record<string, string>>((styles, declaration) => {
      const separatorIndex = declaration.indexOf(':');
      if (separatorIndex === -1) {
        return styles;
      }

      const property = declaration.slice(0, separatorIndex).trim();
      const value = declaration.slice(separatorIndex + 1).trim();

      if (!property || !value) {
        return styles;
      }

      styles[property] = value;
      return styles;
    }, {});
}

export function createDefaultStudioNodeTransform(): StudioNodeTransform {
  return {
    translateX: 0,
    translateY: 0,
    scaleX: 1,
    scaleY: 1,
    rotate: 0,
    skewX: 0,
    skewY: 0
  };
}

export function createDefaultStudioNodeAnimation(): StudioNodeAnimation {
  return {
    name: 'none',
    durationMs: 500,
    delayMs: 0,
    easing: 'ease',
    iterationCount: '1'
  };
}

export function createDefaultStudioNodeStyle(): StudioNodeStyle {
  return {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    alignSelf: 'auto',
    flexGrow: 0,
    flexShrink: 1,
    order: 0,
    gap: '0.75rem',
    width: '100%',
    height: 'auto',
    aspectRatio: 'auto',
    minWidth: '0px',
    maxWidth: '100%',
    minHeight: 'auto',
    maxHeight: 'none',
    flexBasis: 'auto',
    padding: '1rem',
    margin: '0',
    backgroundColor: '#ffffff',
    backgroundImage: '',
    backgroundPosition: 'center',
    backgroundSize: 'auto',
    backgroundRepeat: 'repeat',
    color: 'var(--ion-text-color)',
    fontFamily: 'inherit',
    fontSize: '1rem',
    fontWeight: '500',
    fontStyle: 'normal',
    lineHeight: '1.55',
    letterSpacing: '0',
    textAlign: 'left',
    textTransform: 'none',
    textDecoration: 'none',
    whiteSpace: 'normal',
    wordBreak: 'normal',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'rgba(15, 23, 42, 0.08)',
    borderRadius: '1rem',
    outlineWidth: '0px',
    outlineStyle: 'solid',
    outlineColor: 'transparent',
    boxShadow: 'none',
    filter: 'none',
    backdropFilter: 'none',
    opacity: 1,
    position: 'relative',
    top: '0px',
    right: '0px',
    bottom: '0px',
    left: '0px',
    zIndex: 1,
    overflow: 'visible',
    overflowX: 'visible',
    overflowY: 'visible',
    visibility: 'visible',
    cursor: 'auto',
    pointerEvents: 'auto',
    objectFit: 'cover',
    objectPosition: 'center',
    transform: createDefaultStudioNodeTransform(),
    animation: createDefaultStudioNodeAnimation(),
    customCss: ''
  };
}

export function normalizeStudioNodeTransform(transform: Partial<StudioNodeTransform> | null | undefined): StudioNodeTransform {
  return {
    ...createDefaultStudioNodeTransform(),
    ...(transform ?? {})
  };
}

export function normalizeStudioNodeAnimation(animation: Partial<StudioNodeAnimation> | null | undefined): StudioNodeAnimation {
  return {
    ...createDefaultStudioNodeAnimation(),
    ...(animation ?? {})
  };
}

export function normalizeStudioNodeStyle(style: Partial<StudioNodeStyle> | null | undefined): StudioNodeStyle {
  const defaults = createDefaultStudioNodeStyle();
  const sourceStyle = style ?? {};

  return {
    ...defaults,
    ...sourceStyle,
    transform: normalizeStudioNodeTransform(sourceStyle.transform),
    animation: normalizeStudioNodeAnimation(sourceStyle.animation)
  };
}

export function normalizeStudioNodeTree(node: StudioNode): StudioNode {
  return {
    ...node,
    style: normalizeStudioNodeStyle(node.style),
    children: node.children.map((child) => normalizeStudioNodeTree(child))
  };
}

export function buildStudioNodeStyles(node: StudioNode): Record<string, string | number> {
  const style = normalizeStudioNodeStyle(node.style);
  const usesOffsets = style.position !== 'static';
  const computedStyles: Record<string, string | number> = {
    'box-sizing': 'border-box',
    display: style.display,
    'flex-direction': style.flexDirection,
    'flex-wrap': style.flexWrap,
    'justify-content': style.justifyContent,
    'align-items': style.alignItems,
    'align-self': style.alignSelf,
    'flex-grow': style.flexGrow,
    'flex-shrink': style.flexShrink,
    order: style.order,
    gap: style.gap,
    width: style.width,
    height: style.height,
    'aspect-ratio': style.aspectRatio,
    'min-width': style.minWidth,
    'max-width': style.maxWidth,
    'min-height': style.minHeight,
    'max-height': style.maxHeight,
    'flex-basis': style.flexBasis,
    padding: style.padding,
    margin: style.margin,
    'background-color': style.backgroundColor,
    'background-image': style.backgroundImage,
    'background-position': style.backgroundPosition,
    'background-size': style.backgroundSize,
    'background-repeat': style.backgroundRepeat,
    color: style.color,
    'font-family': style.fontFamily,
    'font-size': style.fontSize,
    'font-weight': style.fontWeight,
    'font-style': style.fontStyle,
    'line-height': style.lineHeight,
    'letter-spacing': style.letterSpacing,
    'text-align': style.textAlign,
    'text-transform': style.textTransform,
    'text-decoration': style.textDecoration,
    'white-space': style.whiteSpace,
    'word-break': style.wordBreak,
    'border-width': style.borderWidth,
    'border-style': style.borderStyle,
    'border-color': style.borderColor,
    'border-radius': style.borderRadius,
    'outline-width': style.outlineWidth,
    'outline-style': style.outlineStyle,
    'outline-color': style.outlineColor,
    'box-shadow': style.boxShadow,
    filter: style.filter,
    'backdrop-filter': style.backdropFilter,
    opacity: style.opacity,
    position: style.position,
    overflow: style.overflow,
    'overflow-x': style.overflowX || style.overflow,
    'overflow-y': style.overflowY || style.overflow,
    visibility: style.visibility,
    cursor: style.cursor,
    'pointer-events': style.pointerEvents,
    'object-fit': style.objectFit,
    'object-position': style.objectPosition,
    top: usesOffsets ? style.top : 'auto',
    right: usesOffsets ? style.right : 'auto',
    bottom: usesOffsets ? style.bottom : 'auto',
    left: usesOffsets ? style.left : 'auto',
    'z-index': style.zIndex,
    transform: buildStudioTransform(style.transform)
  };

  if (style.backgroundImage.trim()) {
    computedStyles['background-image'] = style.backgroundImage;
  }

  const animationName = resolveStudioAnimationName(style.animation.name);
  if (animationName) {
    computedStyles['animation-name'] = animationName;
    computedStyles['animation-duration'] = `${style.animation.durationMs}ms`;
    computedStyles['animation-delay'] = `${style.animation.delayMs}ms`;
    computedStyles['animation-timing-function'] = style.animation.easing;
    computedStyles['animation-iteration-count'] = style.animation.iterationCount;
    computedStyles['animation-fill-mode'] = 'both';
  }

  return {
    ...computedStyles,
    ...parseStudioCustomCss(style.customCss)
  };
}

export function getStudioNodeIcon(nodeType: StudioNodeType): string {
  switch (nodeType) {
    case 'frame':
      return 'scan-outline';
    case 'stack':
      return 'layers-outline';
    case 'text':
      return 'text-outline';
    case 'button':
      return 'radio-button-on-outline';
    case 'media':
      return 'image-outline';
    case 'badge':
      return 'pricetag-outline';
    default:
      return 'shapes-outline';
  }
}

function buildStudioTransform(transform: StudioNodeTransform): string {
  const values = [
    `translate(${transform.translateX}px, ${transform.translateY}px)`,
    `scale(${transform.scaleX}, ${transform.scaleY})`,
    `rotate(${transform.rotate}deg)`,
    `skew(${transform.skewX}deg, ${transform.skewY}deg)`
  ];

  return values.join(' ');
}

function resolveStudioAnimationName(name: StudioAnimationName): string | null {
  switch (name) {
    case 'fade-up':
      return 'bw-studio-fade-up';
    case 'fade-in':
      return 'bw-studio-fade-in';
    case 'pop-in':
      return 'bw-studio-pop-in';
    case 'pulse':
      return 'bw-studio-pulse';
    case 'float':
      return 'bw-studio-float';
    default:
      return null;
  }
}
import { createPrefixedUuidV7 } from '../utils/public-id.utils';
